import { create } from "zustand";
import { persist } from "zustand/middleware";
import loadStore from "@/utils/generateStore";
import { handleErrorMessage, normalizeIdentityUser } from "./authUtils";
import { clearSession, subscribeSessionUpdates } from "./sessionRenewal";
import { bloquearLaCaja } from "@/context/caja/cajeroDeLaCaja";
import { esSesionDeCaja } from "@/context/caja/sesionDeCaja";

// Exportado para que integraciones externas (p.ej. Sentry identity en
// `src/lib/sentryIdentity.ts`) puedan subscribirse a cambios de user.
// El resto del código debe seguir usando los selectores/actions de abajo.
export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      billingSession: 0,
      loading: false,
      success: false,
      error: {
        email: "",
        password: "",
        generic: "",
      },
      socket: null,
      messages: [],

      setLoading: (payload: boolean) => set({ loading: payload }),

      startLogin: () =>
        set({
          user: null,
          success: false,
          error: {
            email: "",
            password: "",
            generic: "",
          },
          loading: true,
        }),

      finishLogin: () =>
        set({
          success: true,
          error: {
            email: "",
            password: "",
            generic: "",
          },
          loading: false,
        }),

      // Mezcla el /me con los claims de UX. Nueva sesión/refresh descarta la
      // caché anterior de billing; el token nunca forma parte de la query key.
      loginSuccess: (payload: any, token?: string | null) => {
        set((state: any) => ({
          user: normalizeIdentityUser(payload, token),
          billingSession: state.billingSession + 1,
        }));
      },

      loginError: (payload: any) =>
        set({
          success: false,
          user: null,
          loading: false,
          error: handleErrorMessage(payload),
          socket: null,
        }),

      cleanErrors: () =>
        set({
          loading: false,
          success: false,
          error: {
            email: "",
            password: "",
            generic: "",
          },
        }),

      logout: async () => {
        const store = await loadStore();
        await clearSession(store);
        // Cerrar sesión bloquea la caja, pero no borra el mandato, los PIN
        // ni la cola (D24 de la caja offline, trd-client#346).
        bloquearLaCaja();

        set({
          user: null,
          loading: false,
          success: false,
          error: {
            email: "",
            password: "",
            generic: "",
          },
          socket: null,
        });
      },

      setSocket: (payload: any) => set({ socket: payload }),

      setMessages: (payload: any) => set({ messages: payload }),

      addMessage: (payload: any) =>
        set((state: any) => {
          const messageExists = state.messages.find(
            (msg: any) => msg.notificationId === payload.notificationId
          );

          if (messageExists) {
            return {
              messages: state.messages,
            };
          }

          return { messages: [...state.messages, payload] };
        }),
    }),
    {
      name: "auth-storage", // nombre para el storage
      // Sólo persistimos el usuario, y no el de la caja sin sesión: al reabrir
      // la app, la caja vuelve a pedir el PIN (trd-client#346).
      partialize: (state: any) => ({
        user: esSesionDeCaja(state.user) ? null : state.user,
      }),
      merge: (persisted: any, current: any) => ({
        ...current,
        user: normalizeIdentityUser(persisted?.user ?? current.user),
      }),
    }
  )
);

subscribeSessionUpdates(({ user, token }) => {
  useAuthStore.setState((state: any) => ({
    user: user ? normalizeIdentityUser(user, token) : null,
    billingSession: state.billingSession + 1,
    loading: false,
  }));
});

// Selectores específicos
export const useAuthUser = () => useAuthStore((state: any) => state.user);
export const useAuthLoading = () => useAuthStore((state: any) => state.loading);
export const useAuthError = () => useAuthStore((state: any) => state.error);
export const useAuthSuccess = () => useAuthStore((state: any) => state.success);
export const useAuthSocket = () => useAuthStore((state: any) => state.socket);
export const useAuthMessages = () =>
  useAuthStore((state: any) => state.messages);

// Selector para acciones
export const useAuthActions = () => {
  const startLogin = useAuthStore((state: any) => state.startLogin);
  const finishLogin = useAuthStore((state: any) => state.finishLogin);
  const loginSuccess = useAuthStore((state: any) => state.loginSuccess);
  const loginError = useAuthStore((state: any) => state.loginError);
  const cleanErrors = useAuthStore((state: any) => state.cleanErrors);
  const logout = useAuthStore((state: any) => state.logout);
  const setLoading = useAuthStore((state: any) => state.setLoading);
  const setSocket = useAuthStore((state: any) => state.setSocket);
  const setMessages = useAuthStore((state: any) => state.setMessages);
  const addMessage = useAuthStore((state: any) => state.addMessage);

  return {
    startLogin,
    finishLogin,
    loginSuccess,
    loginError,
    cleanErrors,
    logout,
    setLoading,
    setSocket,
    setMessages,
    addMessage,
  };
};
